/**
 * Help dialog: short visual demos (real screen recordings of the app, scripts/make-demos.mjs)
 * that play one after another, a "try it" action for each, compact keyboard shortcuts and data
 * sources (with the date of the pre-computed data and the anonymous statistics switch). Also offers
 * the guided tour of the live interface (tour.js) and the install button of the app (pwa.js).
 */
import { $, $$, esc, icon, reducedMotion } from './ui.js';
import { startTour } from './tour.js';
import { labelsBuilt } from './labels.js';
import { act, isOptedOut, setOptOut, statsBlockedBySignal } from './telemetry.js';
import { installState, promptInstall, onInstallChange } from './pwa.js';

const MEDIA = '/assets/media/';
export const DEMOS = [
  { id: 'pick', icon: 'mouse-pointer-click', title: 'Xem quy hoạch tại điểm', sub: 'Nhấp vào bất kỳ vị trí nào', steps: ['Nhấp vào bản đồ', 'Xem chức năng, chỉ tiêu ô đất', 'Mở đồ án, quyết định'] },
  { id: 'search', icon: 'search', title: 'Tìm kiếm thông minh', sub: 'Địa chỉ, toạ độ, mã đồ án', steps: ['Gõ hoặc dán vào ô tìm kiếm', 'Chọn kết quả'], chips: ['Địa chỉ', 'WGS84', 'VN-2000', 'Plus Code', 'Link Google Maps', 'Mã đồ án'] },
  { id: 'transport', icon: 'route', title: 'Quy hoạch giao thông', sub: 'Mạng lưới đường, lộ giới', steps: ['Chọn lớp Quy hoạch giao thông', 'Nhấp vào một tuyến đường', 'Xem lộ giới ước tính, ô đất hai bên'] },
  { id: 'basemap', icon: 'layers-2', title: 'Đổi bản đồ nền', sub: 'Xem trước từng kiểu', steps: ['Mở ô Bản đồ nền góc dưới', 'Rê chuột để xem trước', 'Nhấp để áp dụng'] },
  { id: 'parcel', icon: 'land-plot', title: 'Kiểm tra khu đất', sub: 'Diện tích theo từng chức năng', steps: ['Vẽ ranh hoặc dán bảng toạ độ', 'Khép ranh', 'Phân tích quy hoạch'] },
  { id: 'measure', icon: 'ruler', title: 'Đo khoảng cách, diện tích', sub: 'Chính xác theo VN-2000', steps: ['Chọn công cụ đo', 'Nhấp các điểm', 'Enter để kết thúc'] },
  { id: 'compare', icon: 'columns-2', title: 'So sánh hai lớp', sub: 'Kéo thanh trượt giữa hai bản đồ', steps: ['Bật chế độ so sánh', 'Chọn lớp mỗi bên', 'Kéo thanh trượt'] },
];

const KEYS = [
  ['Bản đồ', [[['/', '|', 'Ctrl', 'K'], 'Tìm kiếm'], [['L'], 'Lớp bản đồ & chú giải'], [['D'], 'Danh mục đồ án'], [['S'], 'Địa điểm đã lưu'], [['P'], 'Chọn lớp quy hoạch'], [['B'], 'Chọn bản đồ nền'], [['1', '…', '7'], 'Chuyển nhanh lớp · 0 để ẩn'], [['T'], 'Giao diện sáng / tối'], [['F'], 'Toàn màn hình'], [['+', '−'], 'Phóng to / thu nhỏ']]],
  ['Công cụ', [[['K'], 'Kiểm tra khu đất'], [['M'], 'Đo khoảng cách'], [['A'], 'Đo diện tích'], [['C'], 'So sánh hai lớp'], [['Enter'], 'Kết thúc vẽ, khép vùng'], [['⌫'], 'Hoàn tác đỉnh vừa vẽ'], [['Esc'], 'Đóng công cụ, bảng thông tin'], [['?'], 'Mở hướng dẫn']]],
];

const SOURCES = [
  ['layout-grid', 'Quy hoạch', 'Hệ thống thông tin quy hoạch TP Hà Nội (quyhoach.hanoi.gov.vn), truy vấn trực tiếp.'],
  ['library-big', 'Danh mục đồ án', 'Quyết định, bản vẽ công bố: Cổng thông tin quy hoạch Bộ Xây dựng, Viện Quy hoạch xây dựng Hà Nội; vị trí gần đúng từ địa giới xã, phường (© OpenStreetMap).'],
  ['map-plus', 'Kế hoạch sử dụng đất', 'Bản đồ KHSDĐ, QHSDĐ cấp huyện của Sở Nông nghiệp và Môi trường Hà Nội (qhkhsdd.hanoi.gov.vn).'],
  ['route', 'Giao thông', 'Đường quy hoạch dựng từ ranh giới ô đất QH phân khu, QH chung; metro, đường sắt: OpenStreetMap; công trình: Overture Maps.'],
  ['store', 'Địa điểm, số nhà', 'Overture Maps Places, OpenStreetMap (cửa hàng, cơ quan, số nhà).'],
  ['map', 'Bản đồ nền', 'OpenFreeMap, OpenMapTiles, © OpenStreetMap; ảnh vệ tinh © Esri, Maxar.'],
  ['search', 'Tìm địa chỉ', 'Esri World Geocoding, Photon (komoot), địa điểm Overture, OpenStreetMap.'],
];

let dlg = null;
let actions = {};
let cur = 0;
let tab = 'demo';
let video = null;

export function initHelp(opts = {}) {
  actions = opts.actions || {};
  dlg = $('#help-modal');
  dlg.classList.add('help-modal');
  dlg.innerHTML = `
    <header class="help-head">
      <div><span class="eyebrow">${icon('sparkles')}Hướng dẫn nhanh</span><h2 id="help-title">Xem thao tác thật trên bản đồ</h2></div>
      <div class="help-head-act">
        <button type="button" class="btn sm subtle" data-act="install" hidden>${icon('download')}<span>Cài<span class="hide-sm"> ứng dụng</span></span></button>
        <button type="button" class="btn sm subtle" data-act="tour">${icon('mouse-pointer-2')}<span>Tham quan<span class="hide-sm"> giao diện</span></span></button>
        <button type="button" class="icon-btn ghost" data-close aria-label="Đóng">${icon('x')}</button>
      </div>
    </header>
    <nav class="seg help-tabs" role="tablist" aria-label="Nội dung hướng dẫn">
      <button type="button" role="tab" data-tab="demo" aria-selected="true">${icon('circle-play')}Demo</button>
      <button type="button" role="tab" data-tab="keys" aria-selected="false">${icon('keyboard')}Phím tắt</button>
      <button type="button" role="tab" data-tab="data" aria-selected="false">${icon('database')}Nguồn dữ liệu</button>
    </nav>
    <div class="help-body" id="help-body"></div>`;
  dlg.addEventListener('click', onClick);
  onInstallChange(syncInstall);
  dlg.addEventListener('keydown', onKey);
  dlg.addEventListener('change', (e) => {
    if (e.target.matches?.('[data-act="stats"]')) setOptOut(!e.target.checked);
  });
  dlg.addEventListener('close', () => { video?.pause(); });
}

const syncInstall = () => { const b = dlg && $('[data-act="install"]', dlg); if (b) b.hidden = installState() === 'installed'; };

export function openHelp(chapter) {
  if (!dlg) return;
  syncInstall();
  if (chapter) { const i = DEMOS.findIndex((d) => d.id === chapter); if (i >= 0) cur = i; }
  tab = 'demo';
  render();
  if (!dlg.open) { dlg.showModal(); act('help', chapter || 'open'); }
  play(cur);
}

function render() {
  $$('.help-tabs [role=tab]', dlg).forEach((t) => t.setAttribute('aria-selected', String(t.dataset.tab === tab)));
  const body = $('#help-body', dlg);
  if (tab === 'demo') {
    body.innerHTML = `
      <div class="help-demo">
        <div class="help-list" role="tablist" aria-orientation="vertical" aria-label="Chọn demo">
          ${DEMOS.map((d, i) => `
            <button type="button" class="hl-item" role="tab" aria-selected="${i === cur}" data-demo="${i}">
              <span class="hl-ico">${icon(d.icon)}</span>
              <span class="hl-text"><b>${esc(d.title)}</b><small>${esc(d.sub)}</small></span>
              <span class="hl-bar" aria-hidden="true"><span></span></span>
            </button>`).join('')}
        </div>
        <figure class="help-stage">
          <div class="stage-frame">
            <video id="help-video" muted playsinline preload="none" disablepictureinpicture aria-label="Video demo"></video>
            <button type="button" class="stage-toggle" data-act="toggle" aria-label="Tạm dừng">${icon('pause')}</button>
            <span class="stage-count" id="stage-count"></span>
          </div>
          <figcaption class="stage-cap" id="stage-cap"></figcaption>
        </figure>
      </div>`;
    video = $('#help-video', dlg);
    video.addEventListener('timeupdate', progress);
    video.addEventListener('ended', () => play((cur + 1) % DEMOS.length));
    video.addEventListener('play', () => syncToggle(true));
    video.addEventListener('pause', () => syncToggle(false));
  } else if (tab === 'keys') {
    video = null;
    body.innerHTML = `<div class="keys-cols">${KEYS.map(([g, list]) => `
      <section><div class="group-title">${esc(g)}</div>
        <div class="keys-list">${list.map(([ks, d]) => `<div class="key-row"><span class="keycaps">${ks.map((k) => (k === '…' ? '<span class="kc-sep">–</span>' : k === '|' ? '<span class="kc-sep">hoặc</span>' : `<kbd class="kc">${esc(k)}</kbd>`)).join('')}</span><span>${esc(d)}</span></div>`).join('')}</div>
      </section>`).join('')}</div>`;
  } else {
    video = null;
    const built = fmtDate(labelsBuilt());
    body.innerHTML = `
      <div class="src-list">${SOURCES.map(([ic, t, d]) => `<div class="src-row"><span class="card-ico">${icon(ic)}</span><div><b>${esc(t)}</b><span>${esc(d)}</span></div></div>`).join('')}</div>
      ${built ? `<p class="src-date">${icon('clock')}Nhãn ô đất tính sẵn cập nhật ngày ${esc(built)}; khu vực chưa có được tính trực tiếp khi xem.</p>` : ''}
      <div class="note warn">${icon('triangle-alert')}<div>Thông tin chỉ có giá trị tham khảo. Để sử dụng chính thức, hãy đề nghị cơ quan quản lý quy hoạch cung cấp thông tin bằng văn bản.</div></div>
      ${privacyHtml()}`;
  }
}

/** Privacy: what is (not) collected + the anonymous statistics switch (Cloudflare build only). */
function privacyHtml() {
  if (document.querySelector('meta[name="qh-backend"]')?.content !== 'edge') return '';
  const signal = statsBlockedBySignal();
  const on = !signal && !isOptedOut();
  return `
    <section class="privacy" aria-labelledby="privacy-title">
      <div class="group-title" id="privacy-title">${icon('shield')}Quyền riêng tư</div>
      <p>Không đăng nhập, không cookie quảng cáo. Địa điểm đã lưu chỉ nằm trên trình duyệt này. Thống kê ẩn danh (tính năng được dùng, tốc độ tải, lỗi) giúp cải thiện ứng dụng: vị trí làm tròn ~1 km, không lưu địa chỉ IP hay mã định danh.</p>
      <label class="toggle-row${signal ? ' disabled' : ''}">
        <input type="checkbox" data-act="stats" ${on ? 'checked' : ''} ${signal ? 'disabled' : ''}>
        <span class="switch" aria-hidden="true"></span>
        <span class="grow">Gửi thống kê sử dụng ẩn danh<small>${signal ? 'Đang tắt theo tín hiệu “Không theo dõi” (Do Not Track / GPC) của trình duyệt; chỉ gửi báo lỗi kỹ thuật.' : 'Khi tắt, ứng dụng không gửi thống kê lẫn báo lỗi.'}</small></span>
      </label>
    </section>`;
}

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(+d) ? '' : d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function play(i) {
  cur = i;
  if (tab !== 'demo' || !video) return;
  const d = DEMOS[i];
  $$('.hl-item', dlg).forEach((b, k) => {
    b.setAttribute('aria-selected', String(k === i));
    b.querySelector('.hl-bar span').style.transform = 'scaleX(0)';
  });
  $('.hl-item[aria-selected=true]', dlg)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  $('#stage-count', dlg).textContent = `${i + 1} / ${DEMOS.length}`;
  $('#stage-cap', dlg).innerHTML = `
    <div class="cap-main">
      <strong>${esc(d.title)}</strong>
      <ol class="cap-steps">${d.steps.map((s, k) => `<li><span>${k + 1}</span>${esc(s)}</li>`).join('')}</ol>
      ${d.chips ? `<div class="cap-chips">${d.chips.map((c) => `<span class="badge">${esc(c)}</span>`).join('')}</div>` : ''}
    </div>
    ${actions[d.id] ? `<button type="button" class="btn primary" data-act="try">Thử ngay${icon('arrow-right')}</button>` : ''}`;
  video.poster = `${MEDIA}demo-${d.id}.webp`;
  video.src = `${MEDIA}demo-${d.id}.mp4`;
  video.currentTime = 0;
  if (reducedMotion()) { syncToggle(false); return; }
  video.play().catch(() => syncToggle(false));
}

function progress() {
  if (!video?.duration) return;
  const bar = $('.hl-item[aria-selected=true] .hl-bar span', dlg);
  if (bar) bar.style.transform = `scaleX(${Math.min(1, video.currentTime / video.duration)})`;
}

function syncToggle(playing) {
  const b = $('.stage-toggle', dlg);
  if (!b) return;
  b.innerHTML = icon(playing ? 'pause' : 'play');
  b.setAttribute('aria-label', playing ? 'Tạm dừng' : 'Phát');
  b.classList.toggle('paused', !playing);
}

function onClick(e) {
  if (e.target === dlg || e.target.closest('[data-close]')) { dlg.close(); return; }
  const t = e.target.closest('[data-tab],[data-demo],[data-act]');
  if (!t) return;
  if (t.dataset.tab) { if (t.dataset.tab !== tab) { video?.pause(); tab = t.dataset.tab; render(); if (tab === 'demo') play(cur); } return; }
  if (t.dataset.demo) { play(+t.dataset.demo); return; }
  switch (t.dataset.act) {
    case 'toggle': if (video) { if (video.paused) video.play().catch(() => {}); else video.pause(); } break;
    case 'try': { const fn = actions[DEMOS[cur].id]; act('help', 'try:' + DEMOS[cur].id); dlg.close(); setTimeout(() => fn?.(), 120); break; }
    case 'tour': act('help', 'tour'); dlg.close(); setTimeout(startTour, 150); break;
    case 'install': promptInstall('help').then(syncInstall); break;
    default:
  }
}

function onKey(e) {
  if (tab !== 'demo') return;
  const item = e.target.closest?.('.hl-item');
  if (item && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
    e.preventDefault();
    const n = (cur + (e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : DEMOS.length - 1)) % DEMOS.length;
    play(n);
    $(`.hl-item[data-demo="${n}"]`, dlg)?.focus();
  } else if (e.key === ' ' && !e.target.closest('button')) {
    e.preventDefault();
    if (video) { if (video.paused) video.play().catch(() => {}); else video.pause(); }
  }
}
